/**
 * API Error utility
 * Creates standardized error objects from API responses
 */

/**
 * Error types for categorizing API errors
 */
export const API_ERROR_TYPES = {
  TIMEOUT: 'timeout',
  NOT_FOUND: 'not_found',
  UNAUTHORIZED: 'unauthorized',
  FORBIDDEN: 'forbidden',
  VALIDATION: 'validation',
  SERVER_ERROR: 'server_error',
  NETWORK: 'network',
  UNKNOWN: 'unknown',
};

/**
 * Creates a standardized API error from a fetch response
 * @param {Response} response - The fetch response object
 * @param {Object} errorData - Parsed error data from response body
 * @returns {Error} - Error object with additional properties
 */
export function createApiError(response, errorData = {}) {
  const error = new Error(
    errorData.message || `Request failed with status: ${response.status}`
  );

  error.status = response.status;
  error.statusText = response.statusText;
  error.data = errorData;

  // Categorize the error type
  if (response.status === 504 || response.status === 408) {
    error.type = API_ERROR_TYPES.TIMEOUT;
    error.isTimeout = true;
  } else if (response.status === 404) {
    error.type = API_ERROR_TYPES.NOT_FOUND;
  } else if (response.status === 401) {
    error.type = API_ERROR_TYPES.UNAUTHORIZED;
  } else if (response.status === 403) {
    error.type = API_ERROR_TYPES.FORBIDDEN;
  } else if (response.status === 400 || response.status === 422) {
    error.type = API_ERROR_TYPES.VALIDATION;
  } else if (response.status >= 500) {
    error.type = API_ERROR_TYPES.SERVER_ERROR;
  } else {
    error.type = API_ERROR_TYPES.UNKNOWN;
  }

  return error;
}

/**
 * Handles a non-ok fetch response by throwing a standardized error
 * @param {Response} response - The fetch response object
 * @throws {Error} - Standardized API error
 */
export async function handleApiResponse(response) {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response;
}

/**
 * Checks if an error is a timeout error
 * @param {Error} error - The error to check
 * @returns {boolean} - True if it's a timeout error
 */
export function isTimeoutError(error) {
  return (
    error.isTimeout ||
    error.type === API_ERROR_TYPES.TIMEOUT ||
    error.status === 504 ||
    error.status === 408 ||
    error.message?.toLowerCase().includes('timeout')
  );
}

/**
 * Checks if an error is a not found error
 * @param {Error} error - The error to check
 * @returns {boolean} - True if it's a not found error
 */
export function isNotFoundError(error) {
  return (
    error.type === API_ERROR_TYPES.NOT_FOUND ||
    error.status === 404
  );
}

/**
 * Checks if an error is a server error
 * @param {Error} error - The error to check
 * @returns {boolean} - True if it's a server error (5xx)
 */
export function isServerError(error) {
  return (
    error.type === API_ERROR_TYPES.SERVER_ERROR ||
    (error.status >= 500 && error.status < 600)
  );
}

